import React, { useCallback, useContext, useEffect, useState } from 'react';
import { TenantIdContext } from '../../../../components/app/App';
import { addTenantUserToGroup, getTenantUsers, removeTenantUserFromGroup } from '../../services/api';
import Heading from '@amzn/meridian/heading';
import Table, { TableRow, TableCell } from '@amzn/meridian/table';
import Button from '@amzn/meridian/button';
import Icon from '@amzn/meridian/icon';
import trashTokens from '@amzn/meridian-tokens/base/icon/trash';
import Input from '@amzn/meridian/input';
import Row from '@amzn/meridian/row';
import plusTokens from '@amzn/meridian-tokens/base/icon/plus-knockout';
import helpTokens from '@amzn/meridian-tokens/base/icon/help';
import Tooltip from '@amzn/meridian/tooltip';
import { EMAIL_REGEX } from '../../../../global/constants';

const UserList = ({ group }) => {
  const [users, setUsers] = useState([]);
  const tenantId = useContext(TenantIdContext);
  const [username, setUsername] = useState('');
  const [usernameInvalid, setUsernameInvalid] = useState(false);
  const [usernameErrorMessage, setUsernameErrorMessage] = useState('');

  const updateUsers = useCallback(() => {
    getTenantUsers(tenantId, group.name).then(response => {
      if (!response?.data) {
        return;
      }
      setUsers(response.data);
    });
  }, [group, tenantId]);

  useEffect(() => {
    updateUsers();
  }, [updateUsers]);

  const handleRemoveClicked = useCallback(
    userName => {
      removeTenantUserFromGroup(tenantId, userName, group.name).then(() => updateUsers());
    },
    [tenantId, group, updateUsers]
  );

  const handleUserAdded = useCallback(() => {
    if (!EMAIL_REGEX.test(username)) {
      setUsernameInvalid(true);
      setUsernameErrorMessage('User must be a valid email address');
      return;
    }
    if (users.indexOf(username) !== -1) {
      setUsernameInvalid(true);
      setUsernameErrorMessage(`User ${username} is already a ${group.display}`);
      return;
    }
    addTenantUserToGroup(tenantId, username, group.name).then(() => {
      setUsername('');
      updateUsers();
      setUsernameInvalid(false);
      setUsernameErrorMessage('');
    });
  }, [tenantId, group, updateUsers, username, users]);

  const handleKeyPress = useCallback(
    e => {
      setUsernameInvalid(false);
      setUsernameErrorMessage('');
      if (e.key === 'Enter') {
        handleUserAdded();
      }
    },
    [handleUserAdded]
  );

  return (
    <div className="m-3">
      <Heading level={4}>
        {group.display}
        <Tooltip position="top" title={group.tooltip}>
          <Icon className="m-1" tokens={helpTokens} />
        </Tooltip>
      </Heading>
      <Table headerRows={0} showStripes={false} showDividers spacing="small">
        {users.map(user => {
          return (
            <TableRow key={user} highlightOnHover>
              <TableCell>{user}</TableCell>
              <TableCell width="100px">
                <Button onClick={() => handleRemoveClicked(user)} type="secondary" size="small">
                  <Icon tokens={trashTokens} /> Remove
                </Button>
              </TableCell>
            </TableRow>
          );
        })}
      </Table>
      <div className="m-3">
        <Row width="100%" widths={['300px', '100px']}>
          <Input
            id="username-input"
            value={username}
            onChange={setUsername}
            type="text"
            placeholder="Enter username"
            size="small"
            onKeyDown={handleKeyPress}
            error={usernameInvalid}
            errorMessage={usernameErrorMessage}
          />
          <Button onClick={handleUserAdded} size="small">
            <Icon tokens={plusTokens} /> Add User
          </Button>
        </Row>
      </div>
    </div>
  );
};

export default UserList;
